/**
 * Tools Service
 * Handles MCP tools management and communication with backend
 * Phase 4 implementation
 */

import { apiClient } from './api';

export interface MCPTool {
  id: string;
  mcp_server_name: string;
  server_url: string;
  transport_type: string;
  metadata: {
    description?: string;
    [key: string]: any;
  };
  tools_list?: any[];
  resources_list?: any[];
  prompts_list?: any[];
  auth_config?: any;
  capabilities?: any;
  status: 'hidden' | 'shown' | 'deprecated' | 'maintenance';
  icon?: string;
  // health_status moved to metadata.health.status
  // pricing_model moved to metadata.pricing
  // compliance_tags moved to metadata.compliance_tags
  created_at?: string;
  updated_at?: string;
  enabled?: boolean;
  selected?: boolean;
}

export interface UserToolConfig {
  id: string;
  tenant_id: string;
  mcp_server_id: string;
  auth_credentials?: any;
  user_config?: any;
  enabled: boolean;
  last_connected?: string;
  usage_stats?: any;
  created_at: string;
  updated_at: string;
}

export interface ToolsConfig {
  mcpServers: {
    [key: string]: {
      type: string;
      url: string;
      metadata?: any;
    };
  };
}

class ToolsService {
  /**
   * Get all available tools from master list
   */
  async getAvailableTools(): Promise<MCPTool[]> {
    try {
      const response = await apiClient.get('/api/external-tools/available');
      // Backend returns {success: true, data: [...], error: null, timestamp: ...}
      // We need to extract the data array
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching available tools:', error);
      throw error;
    }
  }

  /**
   * Get user's selected tools
   */
  async getUserTools(tenantId: string): Promise<MCPTool[]> {
    try {
      const response = await apiClient.get(`/api/external-tools/user/${tenantId}`);
      // Backend returns {success: true, data: [...], error: null, timestamp: ...}
      // We need to extract the data array
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching user tools:', error);
      throw error;
    }
  }

  /**
   * Add a tool to user's selection
   */
  async addUserTool(tenantId: string, mcpServerId: string): Promise<UserToolConfig> {
    try {
      const response = await apiClient.post('/api/external-tools/user', {
        tenant_id: tenantId,
        mcp_server_id: mcpServerId,
        enabled: true
      });
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error adding user tool:', error);
      throw error;
    }
  }

  /**
   * Remove a tool from user's selection
   */
  async removeUserTool(tenantId: string, mcpServerId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/external-tools/user/${tenantId}/${mcpServerId}`);
    } catch (error) {
      console.error('Error removing user tool:', error);
      throw error;
    }
  }

  /**
   * Update user's tool configuration
   */
  async updateUserToolConfig(
    tenantId: string,
    mcpServerId: string,
    config: Partial<UserToolConfig>
  ): Promise<UserToolConfig> {
    try {
      const response = await apiClient.put(`/api/external-tools/user/${tenantId}/${mcpServerId}`, config);
      return response.data;
    } catch (error) {
      console.error('Error updating tool config:', error);
      throw error;
    }
  }

  /**
   * Generate tools configuration for MCP Host
   */
  async generateToolsConfig(tenantId: string): Promise<ToolsConfig> {
    try {
      const response = await apiClient.get(`/api/external-tools/config/${tenantId}`);
      return response.data;
    } catch (error) {
      console.error('Error generating tools config:', error);
      throw error;
    }
  }

  /**
   * Sync tools with MCP Host
   */
  async syncWithMCPHost(tenantId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post(`/api/external-tools/sync/${tenantId}`);
      if (response.data?.success) {
        return {
          success: true,
          message: response.data.data?.message || 'Tools synchronized'
        };
      } else {
        return {
          success: false,
          message: response.data?.message || 'Sync failed'
        };
      }
    } catch (error) {
      console.error('Error syncing with MCP Host:', error);
      return {
        success: false,
        message: 'Failed to sync with MCP Host'
      };
    }
  }

  /**
   * Get tool health status
   */
  async getToolHealth(mcpServerId: string): Promise<{
    status: 'healthy' | 'unhealthy' | 'unknown';
    lastCheck?: string;
    error?: string;
  }> {
    try {
      const response = await apiClient.get(`/api/external-tools/health/${mcpServerId}`);
      return response.data;
    } catch (error) {
      console.error('Error checking tool health:', error);
      throw error;
    }
  }

  /**
   * Test tool connection
   */
  async testConnection(mcpServerId: string, tenantId: string): Promise<{
    success: boolean;
    message: string;
    tools?: any[];
  }> {
    try {
      const response = await apiClient.post('/api/external-tools/test-connection', {
        mcp_server_id: mcpServerId,
        tenant_id: tenantId
      });
      return response.data;
    } catch (error) {
      console.error('Error testing tool connection:', error);
      throw error;
    }
  }

  /**
   * Get tool usage statistics
   */
  async getUsageStats(tenantId: string): Promise<any> {
    try {
      const response = await apiClient.get(`/api/external-tools/stats/${tenantId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching usage stats:', error);
      throw error;
    }
  }
}

export const externalToolsService = new ToolsService();